import { mapColor } from '@/utils/colors';
import clsx from 'clsx';
import { Headline } from '../../typography/headline/headline';
import { Level } from '@/utils/typography';
import { useRef, useState, useEffect } from 'react';
import { SpeechBubbleSvg } from './speech-bubble-svg';

export type SpeechBubbleTextProps = {
    content: string;
    tag?: 'p' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    backgroundColor?: string;
    textColor?: string;
    overlapPrevious?: boolean;
    arrowPosition?: 'bottom-left' | 'bottom-center' | 'bottom-right';
    alignment?: 'left' | 'center' | 'right';
    borderColor?: string;
};

const tagToLevel: Record<string, Level> = { h2: 2, h3: 3, h4: 4, h5: 5, h6: 6 };
const alignmentClasses = { left: 'r-justify-start', center: 'r-justify-center', right: 'r-justify-end' };
const paddingClasses = { small: 'r-px-5 r-py-3', large: 'r-px-12 r-py-6' };

export const SpeechBubbleText = ({
    content,
    tag = 'h2',
    backgroundColor = 'primary',
    textColor = 'white',
    overlapPrevious = false,
    arrowPosition = 'bottom-center',
    alignment = 'center',
    borderColor = 'transparent',
}: SpeechBubbleTextProps) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    const isLarge = tag === 'h2' || tag === 'h3';
    const paddingSize = isLarge ? 'large' : 'small';

    useEffect(() => {
        const el = contentRef.current;
        if (!el) {
            return;
        }

        const updateDimensions = () => {
            const { width, height } = el.getBoundingClientRect();
            setDimensions({ width, height });
        };

        const observer = new ResizeObserver(updateDimensions);
        observer.observe(el);
        updateDimensions();

        return () => observer.disconnect();
    }, []);

    if (!content) {
        return null;
    }

    const bgColor = mapColor(backgroundColor);
    const txtColor = mapColor(textColor);
    const borderColorValue = borderColor === 'transparent' ? null : mapColor(borderColor);

    return (
        <div
            data-component="speech-bubble-text"
            className={clsx('r-flex', alignmentClasses[alignment], {
                'r-relative r-z-10 r-mb-2 -r-translate-y-[calc(50%_+_var(--section-pt,32px))] md:-r-translate-y-[calc(50%_+_var(--section-pt,32px))]':
                    overlapPrevious,
                'r-mb-6': !overlapPrevious,
            })}
        >
            <div className="r-relative r-inline-block">
                {dimensions.width > 0 && (
                    <SpeechBubbleSvg
                        width={dimensions.width}
                        height={dimensions.height}
                        arrowPosition={arrowPosition}
                        paddingSize={paddingSize}
                        fillColor={bgColor}
                        strokeColor={borderColorValue}
                    />
                )}
                <div ref={contentRef} className={clsx('r-relative', paddingClasses[paddingSize])} style={{ color: txtColor || undefined }}>
                    {tag === 'p' ? (
                        <p className="r-m-0" dangerouslySetInnerHTML={{ __html: content }} />
                    ) : (
                        <Headline
                            level={tagToLevel[tag]}
                            tag={tag as 'h2' | 'h3' | 'h4' | 'h5' | 'h6'}
                            color="inherit"
                            margin="none"
                            marginSize="small"
                            size={1}
                            align={null}
                            font="default"
                            index={0}
                            hash=""
                        >
                            {content}
                        </Headline>
                    )}
                </div>
            </div>
        </div>
    );
};
